import { createRoot } from 'react-dom/client';
import { ChallengeManagement } from './features/challenge-management/ChallengeManagement';
import { ChallengeProvider } from './features/challenge-management/context/Challenge';
import { Wrapper } from './common/wrapper';
import { type AppProps } from './common/interfaces';
import { Analytics } from './features/analytics/Analytics';
import { ModeratorActionPanel } from './features/moderator-action/ModeratorActionPanel';
import { ScoreReset } from './features/score-reset/ScoreReset';
import { type ScoreResetView } from './features/score-reset/enums/ScoreResetView';
import { UserScore } from './features/user-score/UserScore';
import { type SubscribeData } from './common/interfaces/SubscribeButton';
import { SubscriptionPanel } from './features/subscribe/SubscriptionPanel';
import { ColorPicker } from './common/components/ColorPicker';
import { ManageLabelsForm } from './features/community-settings/components/ManageLabelsForm';
import { UsersDownload } from './features/users-download/UsersDownload';
import { StatusPage } from './features/status-page/StatusPage';
import { AdmWrapper } from './common/AdmWrapper';
import { SearchInput } from './common/components/SearchInput';
import { PageCmsPreview } from './features/page-cms-preview/PageCmsPreview';
import { type PreviewMode } from './common/enums';
import { RemoveButton } from './common/components/RemoveButton';
import { OnboardBioAndSkills } from './features/profile/components/OnboardBioAndSkills';
import { AnnouncementCalendarForm } from './common/components/AnnouncementCalendarForm';
import { AuthorListPopup } from './common/components/AuthorListPopup';
import { LoginPage } from './features/login/LoginPage';
import { LoginWrapper } from './features/login/LoginWrapper';
import { type LoginPageProps } from './features/login/types';
import { MfaSetupPage } from './features/login/mfa/MfaSetupPage';
import { MfaVerifyPage } from './features/login/mfa/MfaVerifyPage';
import { type MfaSetupPageProps, type MfaVerifyPageProps } from './features/login/mfa/MfaTypes';
import { MfaReset } from './features/mfa-reset/MfaReset';
import { type MfaResetProps } from './features/mfa-reset/MfaReset';

interface ChallengeManagementProps extends AppProps {
  readonly challengeId?: string;
}

export interface AnalyticsProps extends AppProps {
  readonly page: string;
  readonly dataTableViewEnabled: boolean;
}

export interface ModeratorActionProps extends AppProps {
  readonly postId: string;
  readonly originalLocale?: string;
}

export interface ScoreResetProps extends AppProps {
  readonly scoreResetView: ScoreResetView;
  readonly scoreResetUserId: string;
}

export interface UserScoreProps extends AppProps {
  readonly userScoreId: string;
}

export interface SubscriptionPanelProps extends AppProps {
  readonly data: {
    themesData: SubscribeData;
    moderatorThemesData: SubscribeData;
  };
  readonly isModeratorSubscribe: boolean;
  readonly challengeId: string;
}

export interface ColorPickerProps extends AppProps {
  readonly currentColor?: string;
  readonly name?: string;
  readonly onChange?: (color: string) => void;
}

export interface LabelManagerProps extends AppProps {
  readonly items: Record<
    string,
    {
      data: {
        ideastatus_name: string;
        ideastatus_id: string;
        ideastatus_color: string;
        ideastatus_enabled: boolean;
        ideastatus_order: number;
      };
    }
  >;
  readonly closePopUp: () => void;
  readonly callback: () => void;
}

export interface PageCmsPreviewProps extends AppProps {
  readonly pageSettings: string;
  readonly mode: PreviewMode;
  readonly pageId: string;
  readonly translations: string;
}

export interface SearchInputProps extends AppProps {
  readonly endpoint: string;
  readonly label?: string;
  readonly getExcludeQuery?: () => string;
  readonly onSelected?: (option: { label: string; value: string }) => void;
}

export interface RemoveButtonProps extends AppProps {
  readonly onClick?: () => void;
}

export interface OnboardingBioAndSkillsProps extends AppProps {
  readonly bio: { initialValue: string; error?: string };
  readonly skills: { initialValues: string[] };
}

export interface AnnouncementCalendarFormProps extends AppProps {
  announcementStart?: string;
  announcementExpire?: string;
  onChange?: (dates: { start: string | null; end: string | null }) => void;
  errors?: {
    announcement_start?: string;
  };
}

export interface AuthorListPopupProps extends AppProps {
  readonly title: string;
  readonly data: {
    id: string;
    avatarImageUrl: string;
    name: string;
  }[];
  readonly arrowPlacement?: 'top' | 'bottom';
  readonly triggerId: string;
}

declare global {
  interface Window {
    COMPONENTS: {
      challengeManagementApp: (props: ChallengeManagementProps) => void;
      analyticsApp: (props: AnalyticsProps, targetId: string) => void;
      scoreResetApp: (props: ScoreResetProps, targetId: string) => void;
      userScoreApp: (props: UserScoreProps, targetId: string) => void;
      moderatorActionApp: (props: ModeratorActionProps, targetId: string) => void;
      subscriptionPanelApp: (props: SubscriptionPanelProps, targetId: string) => void;
      designPageApp: (props: ColorPickerProps, targetId: string) => void;
      labelManagerApp: (props: LabelManagerProps, targetId: string) => void;
      usersDownloadApp: (props: AppProps, targetId: string) => void;
      statusPageApp: (props: AppProps, targetId: string) => void;
      userSearchApp: (props: SearchInputProps, targetId: string) => void;
      pageCmsPreviewApp: (props: PageCmsPreviewProps, targetId: string) => void;
      removeButtonApp: (props: RemoveButtonProps, targetId: string) => void;
      onboardBioAndSkillsApp: (props: OnboardingBioAndSkillsProps, targetId: string) => void;
      announcementCalendarApp: (props: AnnouncementCalendarFormProps, targetId: string) => void;
      authorListPopupApp: (props: AuthorListPopupProps, targetId: string) => void;
      loginApp: (props: LoginPageProps, targetId: string) => void;
      mfaSetupApp: (props: MfaSetupPageProps, targetId: string) => void;
      mfaVerifyApp: (props: MfaVerifyPageProps, targetId: string) => void;
      mfaResetApp: (props: MfaResetProps & AppProps, targetId: string) => void;
    };
  }
}

export const ChallengeManagementApp = (props: ChallengeManagementProps) => {
  const challengeId = String(props.challengeId);

  return (
    <Wrapper {...props}>
      <ChallengeProvider id={challengeId}>
        <ChallengeManagement />
      </ChallengeProvider>
    </Wrapper>
  );
};

export const AnalyticsApp = (props: AnalyticsProps) => (
  <Wrapper {...props}>
    <Analytics {...props} />
  </Wrapper>
);

export const ModeratorActionApp = (props: ModeratorActionProps) => (
  <Wrapper {...props}>
    <ModeratorActionPanel {...props} />
  </Wrapper>
);

export const ScoreResetApp = (props: ScoreResetProps) => (
  <Wrapper {...props}>
    <ScoreReset {...props} />
  </Wrapper>
);

export const UserScoreApp = (props: UserScoreProps) => (
  <Wrapper {...props}>
    <UserScore {...props} />
  </Wrapper>
);

export const SubscriptionPanelApp = (props: SubscriptionPanelProps) => (
  <Wrapper {...props}>
    <SubscriptionPanel {...props} />
  </Wrapper>
);

export const DesignPageApp = (props: ColorPickerProps) => (
  <Wrapper {...props}>
    <ColorPicker {...props} />
  </Wrapper>
);

export const UsersDownloadApp = (props: AppProps) => (
  <Wrapper {...props}>
    <UsersDownload />
  </Wrapper>
);

const StatusPageApp = (props: AppProps) => (
  <AdmWrapper {...props}>
    <StatusPage {...props} />
  </AdmWrapper>
);

const UserSearchApp = (props: SearchInputProps) => (
  <Wrapper {...props}>
    <SearchInput {...props} />
  </Wrapper>
);

export const PageCmsPreviewApp = (props: PageCmsPreviewProps) => (
  <Wrapper {...props}>
    <PageCmsPreview {...props} />
  </Wrapper>
);

const RemoveButtonApp = (props: RemoveButtonProps) => (
  <Wrapper {...props}>
    <RemoveButton {...props} />
  </Wrapper>
);

const OnboardBioAndSkillsApp = (props: OnboardingBioAndSkillsProps) => (
  <Wrapper {...props}>
    <OnboardBioAndSkills {...props} />
  </Wrapper>
);

const AnnouncementCalendarFormApp = (props: AnnouncementCalendarFormProps) => (
  <AdmWrapper {...props}>
    <AnnouncementCalendarForm {...props} />
  </AdmWrapper>
);

const AuthorListPopupApp = (props: AuthorListPopupProps) => (
  <Wrapper {...props}>
    <AuthorListPopup {...props} />
  </Wrapper>
);

const LoginPageApp = (props: LoginPageProps) => (
  <LoginWrapper locale={props.locale} communityColors={props.communityColors}>
    <LoginPage {...props} />
  </LoginWrapper>
);

const MfaSetupApp = (props: MfaSetupPageProps) => (
  <LoginWrapper locale={props.locale} communityColors={props.communityColors}>
    <MfaSetupPage {...props} />
  </LoginWrapper>
);

const MfaVerifyApp = (props: MfaVerifyPageProps) => (
  <LoginWrapper locale={props.locale} communityColors={props.communityColors}>
    <MfaVerifyPage {...props} />
  </LoginWrapper>
);

const MfaResetApp = (props: MfaResetProps & AppProps) => (
  <Wrapper {...props}>
    <MfaReset {...props} />
  </Wrapper>
);

const domNode = document.createElement('div');
document.querySelector('#main')?.appendChild(domNode);
const root = createRoot(domNode);

window.COMPONENTS = {
  challengeManagementApp: (props: ChallengeManagementProps) => {
    root.render(<ChallengeManagementApp {...props} />);
  },
  analyticsApp: (props: AnalyticsProps, targetId) => {
    const container = document.querySelector(`#${targetId}`);
    container && createRoot(container).render(<AnalyticsApp {...props} />);
  },
  scoreResetApp: (props: ScoreResetProps, targetId) => {
    const container = document.querySelector(`#${targetId}`);
    container && createRoot(container).render(<ScoreResetApp {...props} />);
  },
  pageCmsPreviewApp: (props: PageCmsPreviewProps, targetId) => {
    const container = document.querySelector(`#${targetId}`);
    container && createRoot(container).render(<PageCmsPreviewApp {...props} />);
  },
  userScoreApp: (props: UserScoreProps, targetId) => {
    const container = document.querySelector(`#${targetId}`);
    container && createRoot(container).render(<UserScoreApp {...props} />);
  },
  moderatorActionApp: (props: ModeratorActionProps, targetId) => {
    const container = document.querySelector(`#${targetId}`);
    container && createRoot(container).render(<ModeratorActionApp {...props} />);
  },
  subscriptionPanelApp: (props: SubscriptionPanelProps, targetId) => {
    const container = document.querySelector(`#${targetId}`);
    container && createRoot(container).render(<SubscriptionPanelApp {...props} />);
  },
  designPageApp: (props: ColorPickerProps, targetId: string) => {
    const container = document.querySelector(`#${targetId}`);
    container && createRoot(container).render(<DesignPageApp {...props} />);
  },
  labelManagerApp: (props: LabelManagerProps, targetId: string) => {
    const container = document.querySelector(`#${targetId}`);
    container &&
      createRoot(container).render(
        <Wrapper {...props}>
          <ManageLabelsForm {...props} />
        </Wrapper>
      );
  },
  usersDownloadApp: (props: AppProps, targetId) => {
    const container = document.querySelector(`#${targetId}`);
    container && createRoot(container).render(<UsersDownloadApp {...props} />);
  },
  statusPageApp: (props: AppProps, targetId) => {
    const container = document.querySelector(`#${targetId}`);
    container && createRoot(container).render(<StatusPageApp {...props} />);
  },
  userSearchApp: (props: SearchInputProps, targetId: string) => {
    const container = document.querySelector(`#${targetId}`);
    container && createRoot(container).render(<UserSearchApp {...props} />);
  },
  removeButtonApp: (props: RemoveButtonProps, targetId: string) => {
    const container = document.querySelector(`#${targetId}`);
    container && createRoot(container).render(<RemoveButtonApp {...props} />);
  },
  onboardBioAndSkillsApp: (props: OnboardingBioAndSkillsProps, targetId: string) => {
    const container = document.querySelector(`#${targetId}`);
    container && createRoot(container).render(<OnboardBioAndSkillsApp {...props} />);
  },
  announcementCalendarApp: (props: AnnouncementCalendarFormProps, targetId: string) => {
    const container = document.querySelector(`#${targetId}`);
    container && createRoot(container).render(<AnnouncementCalendarFormApp {...props} />);
  },
  authorListPopupApp: (props: AuthorListPopupProps, targetId: string) => {
    const container = document.querySelector(`#${targetId}`);
    container && createRoot(container).render(<AuthorListPopupApp {...props} />);
  },
  loginApp: (props: LoginPageProps, targetId: string) => {
    const container = document.querySelector(`#${targetId}`);
    container && createRoot(container).render(<LoginPageApp {...props} />);
  },
  mfaSetupApp: (props: MfaSetupPageProps, targetId: string) => {
    const container = document.querySelector(`#${targetId}`);
    container && createRoot(container).render(<MfaSetupApp {...props} />);
  },
  mfaVerifyApp: (props: MfaVerifyPageProps, targetId: string) => {
    const container = document.querySelector(`#${targetId}`);
    container && createRoot(container).render(<MfaVerifyApp {...props} />);
  },
  mfaResetApp: (props: MfaResetProps & AppProps, targetId: string) => {
    const container = document.querySelector(`#${targetId}`);
    container && createRoot(container).render(<MfaResetApp {...props} />);
  }
};
